import envRaw from './env-raw'
import StrictObj from './strict-obj'
import MapSimple from './map-simple'
import extractRawElm from './extract-raw-elm'

import { offEvent, onEventPassive } from './on-event'

const rafsTop = MapSimple()
const rafsLeft = MapSimple()

const SCROLL_TOP = 'scrollTop'
const SCROLL_LEFT = 'scrollLeft'

export default StrictObj({
  top: envRaw.useNativeSmoothScroll ? nativeTop : fallbackTop,
  left: envRaw.useNativeSmoothScroll ? nativeLeft : fallbackLeft,
  nativeTop,
  nativeLeft,
  fallbackTop,
  fallbackLeft
})

function nativeTop($elm, goalPos, _delay, _easeName) {
  const elm = extractRawElm($elm)
  elm.scroll({
    top: goalPos,
    left: elm.scrollLeft,
    behavior: 'smooth'
  })
  return $elm
}

function nativeLeft($elm, goalPos, _delay, _easeName) {
  const elm = extractRawElm($elm)
  elm.scroll({
    top: elm.scrollTop,
    left: goalPos,
    behavior: 'smooth'
  })
  return $elm
}

// fallback

function cancelRaf(rafs, $elm) {
  const elm = extractRawElm($elm)
  const rafIndex = rafs.getIndex(elm)
  if (rafIndex !== -1) {
    const raf = rafs.getByIndex(rafIndex)
    cancelAnimationFrame(raf)
    rafs.delByIndex(rafIndex)
  }

  return $elm
}

function fallbackTop($elm, goalPos, delay, easeName) {
  return fallback(SCROLL_TOP, rafsTop, $elm, goalPos, delay, easeName)
}

function fallbackLeft($elm, goalPos, delay, easeName) {
  return fallback(SCROLL_LEFT, rafsLeft, $elm, goalPos, delay, easeName)
}

function fallback(scrollDir, rafs, $elm, goalPos, delay, easeName = 'linear') {
  cancelRaf(rafs, $elm)

  const elm = extractRawElm($elm)
  const easeFnc = easings[easeName]

  let startPos = null
  let currPos = null

  let startTime = null
  let goalTime = null

  const initRaf = requestAnimationFrame(initCallback)
  const currRafIndex = rafs.setAndGetIndex(elm, initRaf)

  onEventPassive($elm, 'touchstart', cancel)

  return $elm

  function cancel() {
    const currRaf = rafs.getByIndex(currRafIndex)
    cancelAnimationFrame(currRaf)
    rafs.delByIndex(currRafIndex)
    elm.style.willChange = null
    offEvent($elm, 'touchstart', cancel)
  }

  function finalize() {
    elm[scrollDir] = goalPos
    cancel()
  }

  function initCallback(now) {
    startPos = Math.round(elm[scrollDir])
    currPos = startPos

    if (delay === 0 || startPos === goalPos) {
      finalize()
      return
    }

    startTime = now
    goalTime = now + delay

    const firstScrollRaf = requestAnimationFrame(rafCallback)
    rafs.replaceByIndex(currRafIndex, firstScrollRaf)
    elm.style.willChange = 'scroll-position'
  }

  function rafCallback(currTime) {
    if (currTime > goalTime) {
      finalize()
      return
    }

    if (Math.round(elm[scrollDir]) !== currPos) {
      cancel()
      return
    }

    const percent = 1 - Math.max(0, startTime + delay - currTime) / delay
    const easePrc = easeFnc(percent, delay * percent, 0, 1, delay)
    currPos = Math.round(easePrc * (goalPos - startPos) + startPos)
    elm[scrollDir] = currPos

    const scrollRaf = requestAnimationFrame(rafCallback)
    rafs.replaceByIndex(currRafIndex, scrollRaf)
  }
}

/*
 easing functions in action: https://easings.net/
 implementation stolen from: https://github.com/KingSora/OverlayScrollbars/blob/master/js/OverlayScrollbars.js

 x : current percent (0 - 1),
 t : current time (duration * percent),
 b : start value (from),
 c : end value (to),
 d : duration
*/

/* eslint-disable curly */

const easings = {
  swing(x, _t, _b, _c, _d) {
    return 0.5 - Math.cos(x * Math.PI) / 2
  },
  linear(x, _t, _b, _c, _d) {
    return x
  },
  easeInQuad(_x, t, b, c, d) {
    return c * (t /= d) * t + b
  },
  easeOutQuad(_x, t, b, c, d) {
    return -c * (t /= d) * (t - 2) + b
  },
  easeInOutQuad(_x, t, b, c, d) {
    if ((t /= d / 2) < 1)
      return c / 2 * t * t + b
    else
      return -c / 2 * ((--t) * (t - 2) - 1) + b
  },
  easeInCubic(_x, t, b, c, d) {
    return c * (t /= d) * t * t + b
  },
  easeOutCubic(_x, t, b, c, d) {
    return c * ((t = t / d - 1) * t * t + 1) + b
  },
  easeInOutCubic(_x, t, b, c, d) {
    if ((t /= d / 2) < 1)
      return c / 2 * t * t * t + b
    else
      return c / 2 * ((t -= 2) * t * t + 2) + b
  },
  easeInQuart(_x, t, b, c, d) {
    return c * (t /= d) * t * t * t + b
  },
  easeOutQuart(_x, t, b, c, d) {
    return -c * ((t = t / d - 1) * t * t * t - 1) + b
  },
  easeInOutQuart(_x, t, b, c, d) {
    if ((t /= d / 2) < 1)
      return c / 2 * t * t * t * t + b
    else
      return -c / 2 * ((t -= 2) * t * t * t - 2) + b
  },
  easeInQuint(_x, t, b, c, d) {
    return c * (t /= d) * t * t * t * t + b
  },
  easeOutQuint(_x, t, b, c, d) {
    return c * ((t = t / d - 1) * t * t * t * t + 1) + b
  },
  easeInOutQuint(_x, t, b, c, d) {
    if ((t /= d / 2) < 1)
      return c / 2 * t * t * t * t * t + b
    else
      return c / 2 * ((t -= 2) * t * t * t * t + 2) + b
  },
  easeInSine(_x, t, b, c, d) {
    return -c * Math.cos(t / d * (Math.PI / 2)) + c + b
  },
  easeOutSine(_x, t, b, c, d) {
    return c * Math.sin(t / d * (Math.PI / 2)) + b
  },
  easeInOutSine(_x, t, b, c, d) {
    return -c / 2 * (Math.cos(Math.PI * t / d) - 1) + b
  },
  easeInExpo(_x, t, b, c, d) {
    if (t === 0)
      return b
    else
      return c * Math.pow(2, 10 * (t / d - 1)) + b
  },
  easeOutExpo(_x, t, b, c, d) {
    if (t === d)
      return b + c
    else
      return c * (-Math.pow(2, -10 * t / d) + 1) + b
  },
  easeInOutExpo(_x, t, b, c, d) {
    if (t === 0)
      return b
    else if (t === d)
      return b + c
    else if ((t /= d / 2) < 1)
      return c / 2 * Math.pow(2, 10 * (t - 1)) + b
    else
      return c / 2 * (-Math.pow(2, -10 * --t) + 2) + b
  },
  easeInCirc(_x, t, b, c, d) {
    return -c * (Math.sqrt(1 - (t /= d) * t) - 1) + b
  },
  easeOutCirc(_x, t, b, c, d) {
    return c * Math.sqrt(1 - (t = t / d - 1) * t) + b
  },
  easeInOutCirc(_x, t, b, c, d) {
    if ((t /= d / 2) < 1)
      return -c / 2 * (Math.sqrt(1 - t * t) - 1) + b
    else
      return c / 2 * (Math.sqrt(1 - (t -= 2) * t) + 1) + b
  },
  easeInBack(_x, t, b, c, d, s = 1.70158) {
    return c * (t /= d) * t * ((s + 1) * t - s) + b
  },
  easeOutBack(_x, t, b, c, d, s = 1.70158) {
    return c * ((t = t / d - 1) * t * ((s + 1) * t + s) + 1) + b
  },
  easeInOutBack(_x, t, b, c, d, s = 1.70158) {
    if ((t /= d / 2) < 1)
      return c / 2 * (t * t * (((s *= (1.525)) + 1) * t - s)) + b
    else
      return c / 2 * ((t -= 2) * t * (((s *= (1.525)) + 1) * t + s) + 2) + b
  }
}
